import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';

// Global 401 interceptor: expired/invalid JWT → clear session + redirect to login
;(function () {
  const _fetch = window.fetch;
  window.fetch = async function (resource, init) {
    const res = await _fetch(resource, init);
    if (res.status === 401) {
      const url = typeof resource === 'string' ? resource
                : resource instanceof URL       ? resource.href
                : resource.url || '';
      // Login endpoints: 401 = wrong credentials, not an expired session — skip
      // Payment endpoints: a payment provider error must NEVER log the user out
      if (url.includes('/login') || url.includes('/api/paiement/')) return res;

      const path = window.location.pathname;
      // Already on a login page — skip to avoid redirect loops
      if (path.startsWith('/connexion') || path.startsWith('/nmdf-direction-k7x2') || path.startsWith('/nmdf-enseignant-m4p9')) return res;

      // Expired session mid-use: clear and redirect to role-appropriate login
      const role = localStorage.getItem('nomedif_role');
      ['nomedif_token', 'nomedif_apprenant', 'nomedif_formateur', 'nomedif_admin', 'nomedif_role']
        .forEach(k => localStorage.removeItem(k));
      if (role === 'admin')          window.location.replace('/nmdf-direction-k7x2?session=expired');
      else if (role === 'formateur') window.location.replace('/nmdf-enseignant-m4p9?session=expired');
      else                           window.location.replace('/connexion?session=expired');
    }
    return res;
  };
}());

const Accueil                 = lazy(() => import('./pages/Accueil'));
const Catalogue               = lazy(() => import('./pages/Catalogue'));
const Formation               = lazy(() => import('./pages/Formation'));
const Apropos                 = lazy(() => import('./pages/Apropos'));
const Contact                 = lazy(() => import('./pages/Contact'));
const PaiementSucces          = lazy(() => import('./pages/PaiementSucces'));
const Admin                   = lazy(() => import('./pages/Admin'));
const Inscription             = lazy(() => import('./pages/Inscription'));
const Connexion               = lazy(() => import('./pages/Connexion'));
const Dashboard               = lazy(() => import('./pages/Dashboard'));
const Satisfaction            = lazy(() => import('./pages/Satisfaction'));
const Auditeur                = lazy(() => import('./pages/Auditeur'));
const Qualiopi                = lazy(() => import('./pages/Qualiopi'));
const Valeurs                 = lazy(() => import('./pages/Valeurs'));
const MentionsLegales         = lazy(() => import('./pages/MentionsLegales'));
const CGV                     = lazy(() => import('./pages/CGV'));
const FAQ                     = lazy(() => import('./pages/FAQ'));
const Temoignages             = lazy(() => import('./pages/Temoignages'));
const Blog                    = lazy(() => import('./pages/Blog'));
const MotDePasseOublie        = lazy(() => import('./pages/MotDePasseOublie'));
const ReinitialiserMotDePasse = lazy(() => import('./pages/ReinitialiserMotDePasse'));
const CreerMotDePasse         = lazy(() => import('./pages/CreerMotDePasse'));
const Confidentialite         = lazy(() => import('./pages/Confidentialite'));
const GoogleCallback          = lazy(() => import('./pages/GoogleCallback'));
const VerifierEmail           = lazy(() => import('./pages/VerifierEmail'));
const FormateurDashboard      = lazy(() => import('./pages/FormateurDashboard'));
const NotFound                = lazy(() => import('./pages/NotFound'));

const WhatsAppButton = lazy(() => import('./components/WhatsAppButton'));
const CookieBanner   = lazy(() => import('./components/CookieBanner'));

function ScrollProgressBar() {
  const barRef = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      if (barRef.current) {
        barRef.current.style.transform = `scaleX(${total > 0 ? el.scrollTop / total : 0})`;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div
      ref={barRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        transform: 'scaleX(0)',
        transformOrigin: '0%',
        background: 'linear-gradient(90deg, #1E40AF 0%, #7c3aed 50%, #3B82F6 100%)',
        zIndex: 9999,
      }}
    />
  );
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#f0f4ff' }}>
      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Admin/formateur are confined to their own dashboard — they never see the public marketing site.
function PublicOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user?.role === 'admin')     return <Navigate to="/nmdf-direction-k7x2" replace />;
  if (user?.role === 'formateur') return <Navigate to="/nmdf-enseignant-m4p9" replace />;
  return children;
}

function App() {
  useEffect(() => {
    let cleanup;
    import('lenis').then(({ default: Lenis }) => {
      const lenis = new Lenis({
        duration: 1.2,
        easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });
      let rafId;
      function raf(time) { lenis.raf(time); rafId = requestAnimationFrame(raf); }
      rafId = requestAnimationFrame(raf);
      cleanup = () => { cancelAnimationFrame(rafId); lenis.destroy(); };
    });
    return () => { if (cleanup) cleanup(); };
  }, []);

  return (
    <AuthProvider>
      <Router>
          <ScrollProgressBar />
          <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-grow">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/"                          element={<PublicOnlyRoute><Accueil /></PublicOnlyRoute>} />
                  <Route path="/catalogue"                  element={<PublicOnlyRoute><Catalogue /></PublicOnlyRoute>} />
                  <Route path="/formation/:id"              element={<PublicOnlyRoute><Formation /></PublicOnlyRoute>} />
                  <Route path="/apropos"                    element={<PublicOnlyRoute><Apropos /></PublicOnlyRoute>} />
                  <Route path="/contact"                    element={<PublicOnlyRoute><Contact /></PublicOnlyRoute>} />
                  <Route path="/paiement-succes"            element={<PublicOnlyRoute><PaiementSucces /></PublicOnlyRoute>} />
                  <Route path="/nmdf-direction-k7x2"         element={<Admin />} />
                  <Route path="/inscription"                element={<PublicOnlyRoute><Inscription /></PublicOnlyRoute>} />
                  <Route path="/connexion"                  element={<PublicOnlyRoute><Connexion /></PublicOnlyRoute>} />
                  <Route path="/dashboard"                  element={<PublicOnlyRoute><Dashboard /></PublicOnlyRoute>} />
                  <Route path="/satisfaction/:paiement_id" element={<PublicOnlyRoute><Satisfaction /></PublicOnlyRoute>} />
                  <Route path="/auditeur"                   element={<PublicOnlyRoute><Auditeur /></PublicOnlyRoute>} />
                  <Route path="/qualiopi"                   element={<PublicOnlyRoute><Qualiopi /></PublicOnlyRoute>} />
                  <Route path="/valeurs"                    element={<PublicOnlyRoute><Valeurs /></PublicOnlyRoute>} />
                  <Route path="/mentions-legales"           element={<PublicOnlyRoute><MentionsLegales /></PublicOnlyRoute>} />
                  <Route path="/cgv"                        element={<PublicOnlyRoute><CGV /></PublicOnlyRoute>} />
                  <Route path="/faq"                        element={<PublicOnlyRoute><FAQ /></PublicOnlyRoute>} />
                  <Route path="/temoignages"                element={<PublicOnlyRoute><Temoignages /></PublicOnlyRoute>} />
                  <Route path="/blog"                       element={<PublicOnlyRoute><Blog /></PublicOnlyRoute>} />
                  <Route path="/mot-de-passe-oublie"        element={<PublicOnlyRoute><MotDePasseOublie /></PublicOnlyRoute>} />
                  <Route path="/reinitialiser-mot-de-passe" element={<PublicOnlyRoute><ReinitialiserMotDePasse /></PublicOnlyRoute>} />
                  <Route path="/creer-mot-de-passe"         element={<PublicOnlyRoute><CreerMotDePasse /></PublicOnlyRoute>} />
                  <Route path="/confidentialite"            element={<PublicOnlyRoute><Confidentialite /></PublicOnlyRoute>} />
                  <Route path="/auth/google/callback"       element={<PublicOnlyRoute><GoogleCallback /></PublicOnlyRoute>} />
                  <Route path="/verifier-email"             element={<PublicOnlyRoute><VerifierEmail /></PublicOnlyRoute>} />
                  <Route path="/nmdf-enseignant-m4p9"         element={<FormateurDashboard />} />
                  <Route path="*"                           element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
          <Suspense fallback={null}>
            <WhatsAppButton />
            <CookieBanner />
          </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
